import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Chip, ContentCard, Row, Tooltip } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import useTextOverflowTooltip from "./useTextOverflowTooltip";
import styles from "./TransferTilesMenu.module.scss";

type LongTransferTileProps = {
  redirect: string;
  icon: string;
  header: string;
  subHeader: string;
  showInstantBadge: boolean;
  badge?: ReactNode;
  onClick?: () => void;
};

const LongTransferTile = ({
  redirect,
  icon,
  header,
  subHeader,
  showInstantBadge,
  badge,
  onClick,
}: LongTransferTileProps) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { isOverflowing, textRef, tooltipMaxWidth } = useTextOverflowTooltip({
    dependency: subHeader,
  });

  return (
    <ContentCard
      kind="button"
      paddingSize="m"
      radiusSize="m"
      onClick={() => {
        onClick?.();
        navigate(redirect);
      }}
    >
      <Row alignItems="center" gapSize="s">
        <Row.Item shrink>
          <div
            className={`nds-icon-button nds-icon-button--themed ${styles.IconWrapper}`}
          >
            <span
              className={`${icon}`}
              aria-hidden="true"
              style={{ fontSize: "20px" }}
            />
          </div>
        </Row.Item>
        <Row.Item className={styles.tileTextItem}>
          <div className={styles.tileTextContent}>
            <div className={styles.tileTextHeaderRow}>
              <div
                className={`fontSize--18px fontWeight--semibold ${styles.tileTextHeader}`}
              >
                {header}
              </div>
              {badge}
            </div>
            {isOverflowing ? (
              <Tooltip
                maxWidth={tooltipMaxWidth}
                text={`${subHeader}`}
                side="bottom"
                wrapperDisplay="block"
              >
                <p
                  ref={textRef}
                  className={`fontSize--16px ${styles.tileTextSubHeader}`}
                >
                  {subHeader}
                </p>
              </Tooltip>
            ) : (
              <p
                ref={textRef}
                className={`fontSize--16px ${styles.tileTextSubHeader}`}
              >
                {subHeader}
              </p>
            )}
          </div>
        </Row.Item>
        <Row.Item />
        {showInstantBadge && (
          <Tooltip text={l10n.getString("transfer-tiles-instant-options")}>
            <Chip
              kind="success"
              isLabelVisible={false}
              label=""
              endIcon="zap"
            />
          </Tooltip>
        )}
        <Row.Item shrink>
          <i
            className={`narmi-icon-chevron-right ${styles.chevronIcon}`}
            style={{ fontSize: "24px" }}
          />
        </Row.Item>
      </Row>
    </ContentCard>
  );
};

export default LongTransferTile;
